import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetTokenUsage } from '@/entities/ailogs/api/generated/api';
import {
  getGetProjectsProjectIdQueryKey,
  getGetProjectsQueryKey,
  getListReferenceMatchDocumentRecordsQueryKey,
  useCreateReferenceMatch,
  useGetReferenceMatch,
} from '@/entities/project/api/generated/api';
import type { ProjectReferenceMatchDetailResponseStatus } from '@/entities/project/api/generated/model';
import { useIsAdmin } from '@/entities/session/model/useIsAdmin';
import { isTokenLimitError } from '@/entities/system/lib/error-guards';
import { useReferenceMatchJudgementMutation } from '@/features/ai-matching-results/lib/useReferenceMatchJudgementMutation';
import { resolveBillingPeriodType } from '@/shared/lib/billing-period-type/resolveBillingPeriodType';
import { showTokenLimitExceededToast } from '@/shared/lib/toast-helpers';
import { toast } from '@/shared/ui';
import { mapDocumentRecordsToCandidates } from './mapDocumentRecordsToCandidates';
import { trackReferenceChatExecuted } from './track-reference-chat';
import type { ProjectReferenceChatType } from './types';

const POLLING_INTERVAL_MS = 5000;

// pending / generating の間のみマッチ生成中とみなしポーリングを継続する。
// idle（未実行）は含めない（含めると未実行カードが無限にポーリングしてしまう）。
const WAITING_STATUSES = new Set<ProjectReferenceMatchDetailResponseStatus>([
  'pending',
  'generating',
]);

type ViewStatus = 'not-executed' | 'loading' | 'completed' | 'failed-network' | 'failed-server';

/**
 * 事業詳細マッチング tab 用の参照マッチ取得フック（インライン版）。
 *
 * サイドパネル版との違い:
 * - idle 状態での自動実行をしない（手動トリガーのみ）
 * - 既存のマッチ結果がある場合（reference_match_id が non-null）は自動で結果を取得する
 *
 * 取得は新API `useGetReferenceMatch`。未作成でも 200（reference_match_id=null）が返るため、
 * 常時 enabled とし、reference_match_id と手動実行フラグ（hasTriggered）で表示状態を導出する。
 */
export const useInlineReferenceChat = ({
  projectId,
  chatType,
}: {
  projectId: string;
  chatType: ProjectReferenceChatType;
}) => {
  // このセッションで実行ボタンを押したか（既存マッチの自動表示とは別管理）
  const [hasTriggered, setHasTriggered] = useState(false);
  const [isTokenLimitExceeded, setIsTokenLimitExceeded] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // このセッションで実行した結果（作成APIが返した reference_match_id）が completed になったら
  // 一度だけキャッシュ無効化するため、無効化待ちの ID を保持する。
  const pendingInvalidationMatchIdRef = useRef<string | null>(null);

  // 右サイドメニューは常時マウントされ projectId 差し替えで remount されないため、
  // 対象事業が変わったらセッション状態（実行フラグ・無効化待ち ID）をリセットする。
  // これを怠ると、前事業の実行状態が次事業に引き継がれ、未実行事業が loading 表示になったり
  // 別事業の completed を誤って無効化したりする。
  const [trackedProjectId, setTrackedProjectId] = useState(projectId);
  if (trackedProjectId !== projectId) {
    setTrackedProjectId(projectId);
    setHasTriggered(false);
    setIsTokenLimitExceeded(false);
    pendingInvalidationMatchIdRef.current = null;
  }

  // トークン使用量（契約サイクル判定用）
  // /ai-logs/token-usage は管理者専用 API のため admin 確定時のみ enabled
  const isAdmin = useIsAdmin();
  const { data: tokenUsage } = useGetTokenUsage({
    query: { enabled: isAdmin === true },
  });

  const {
    data: referenceMatch,
    isError,
    isPending,
    refetch,
  } = useGetReferenceMatch(projectId, chatType, {
    query: {
      refetchInterval: (query) => {
        const status = query.state.data?.status;
        return status && WAITING_STATUSES.has(status) ? POLLING_INTERVAL_MS : false;
      },
      refetchOnWindowFocus: false,
    },
  });

  // このセッションで実行した結果（pendingInvalidationMatchIdRef の reference_match_id）が
  // completed になった時点で、その種別の document-records が確定する。確定タイミングで
  // 関連キャッシュを invalidate し、財源一覧・事業詳細サイドパネル
  // （listReferenceMatchDocumentRecords）と、事業一覧・事業詳細の「AI分析済み」表示
  // （reference_chats を含む getProjects 系）を最新化する。
  //
  // 完了の identity に reference_match_id を使うことで、以下を正しく扱う:
  // - 待機中（pending/generating）を観測せず初回 refetch が直接 completed を返すケース（高速処理・mock）
  // - 再実行で旧データも新データも completed のまま reference_match_id だけ入れ替わるケース
  //   （backend は再実行時に旧 ReferenceMatch を DELETED にして新 UUID を採番する）
  // 既存の完了済みマッチをマウント時に表示するだけ（実行トリガーなし）の場合は
  // pendingInvalidationMatchIdRef が null のため発火しない。
  useEffect(() => {
    const pendingId = pendingInvalidationMatchIdRef.current;
    if (
      pendingId &&
      referenceMatch?.status === 'completed' &&
      referenceMatch.reference_match_id === pendingId
    ) {
      // 同一 ID での重複無効化を防ぐためクリアする（一度だけ実行）
      pendingInvalidationMatchIdRef.current = null;
      queryClient.invalidateQueries({
        queryKey: getListReferenceMatchDocumentRecordsQueryKey(),
      });
      queryClient.invalidateQueries({ queryKey: getGetProjectsProjectIdQueryKey(projectId) });
      queryClient.invalidateQueries({ queryKey: getGetProjectsQueryKey() });
    }
  }, [referenceMatch?.status, referenceMatch?.reference_match_id, projectId, queryClient]);

  // 既存マッチがある or このセッションで実行した場合に「実行済み」とみなす
  const isActivated = hasTriggered || referenceMatch?.reference_match_id != null;

  // suppressToast: true により、共通インターセプタが 402 で表示する簡易トーストを抑制し、
  // 詳細トースト（showTokenLimitExceededToast）の二重表示を防ぐ。
  const createMutation = useCreateReferenceMatch({ request: { suppressToast: true } });

  const { updateJudgement } = useReferenceMatchJudgementMutation({
    projectId,
    referenceChatType: chatType,
  });

  // 新規作成API呼び出し + ポーリング開始
  const execute = async () => {
    if (isTokenLimitExceeded) return;
    // 再実行判別は MatchingTabContent の renderTriggerButton と同じ規約
    // （完了済み or サーバーエラー後の再 execute は「再実行」扱い）
    const isReExecution = viewStatus === 'completed' || viewStatus === 'failed-server';
    setHasTriggered(true);
    try {
      const { id } = await createMutation.mutateAsync({
        projectId,
        data: { type: chatType },
      });
      // この実行で作成された reference_match_id を無効化待ちに登録する。
      // 以後この ID が completed になった時点で一度だけキャッシュを無効化する。
      pendingInvalidationMatchIdRef.current = id;
      trackReferenceChatExecuted({ chatType, isReExecution, projectId });
      await refetch();
    } catch (error) {
      if (isTokenLimitError(error)) {
        // 他の AI 機能（査定 AI 一括実行・事業一括作成・AI スキル一括実行）と挙動を揃える:
        // インラインのエラーテキストは出さず、トークン上限トーストで通知する。
        // ボタンは isTokenLimitExceeded で disabled のまま据え置く。
        setIsTokenLimitExceeded(true);
        // ポーリング起動済みの取得を停止し、表示を「未実行」に戻す。
        // 既存マッチがある場合は reference_match_id が non-null のため自動で表示が維持される。
        setHasTriggered(false);
        showTokenLimitExceededToast({
          toastId: `inline-reference-chat-${chatType}-402`,
          navigate,
          billingPeriodType: resolveBillingPeriodType(tokenUsage?.plan.billing_period_type),
        });
        return;
      }
      // 402 以外の失敗（バリデーション・他タブで実行中・一時的な 5xx 等）:
      // suppressToast でグローバルトーストを抑制しているため明示的にエラーを通知し、
      // hasTriggered を解除して「未実行」へ戻す（idle はポーリングされないため loading に貼り付かないようにする）。
      // 既存マッチがある場合は reference_match_id が non-null のため結果表示が維持される。
      setHasTriggered(false);
      toast.error(t('ai.errors.analysis-failed-description'));
    }
  };

  // GETの再取得のみ（ネットワーク一時障害からの復帰用）
  const retry = async () => {
    await refetch();
  };

  const viewStatus: ViewStatus = (() => {
    // GET 失敗・初回ロードを isActivated 判定より先に評価する。
    // （常時 enabled のため、既存マッチがある事業で初回 GET が失敗したケースを
    //   not-executed ではなく failed-network（再読み込み導線）として表示するため）
    if (isError) return 'failed-network';
    if (isPending) return 'loading';
    if (referenceMatch?.status === 'failed') return 'failed-server';
    if (referenceMatch?.status === 'completed') return 'completed';
    // deleted は WAITING_STATUSES 外でポーリングされないため、loading に貼り付かないよう未実行扱いにする
    if (referenceMatch?.status === 'deleted') return 'not-executed';
    if (!isActivated) return 'not-executed';
    // active（実行直後 or 既存マッチ）かつ idle/pending/generating → 生成待ち
    return 'loading';
  })();

  const candidates = mapDocumentRecordsToCandidates(referenceMatch?.documents ?? []);

  return {
    candidates,
    updateJudgement,
    viewStatus,
    execute,
    retry,
    isExecuting: createMutation.isPending,
    isTokenLimitExceeded,
  };
};
